import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Calendar, Linkedin, MessageCircle } from 'lucide-react';

const HeroSection = () => {
  const openWhatsApp = () => {
    const message = `Hello Shunmugavel! 🎯

I visited your ShansoConsult website and I'm interested in learning about your expertise in:

✅ Process Excellence & Lean Implementation (30-40% efficiency gains)
✅ AI/RPA Automation & Risk Management (100% compliance rate)  
✅ AI Management Systems (40% manual process reduction)
✅ Process Excellence & Risk Management

I would appreciate a consultation to discuss how your 21+ years of experience can help transform our operations.

When would be a good time to connect?

Thank you!`;
    
    const whatsappUrl = `https://wa.me/919962536379?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section className="hero-section-redesigned relative">
      <div className="max-w-7xl mx-auto">
        {/* Modern Hero Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center min-h-screen py-8 lg:py-0">
          {/* Left side - Enhanced Content (7 columns) */}
          <div className="lg:col-span-7 text-center lg:text-left px-4 lg:px-0">
            {/* Trust badges removed as requested */}
            
            <h1 className="hero-title mb-6">
              <span className="hero-title-line">Driving Compliance, Innovation</span>
              <span className="hero-title-line">& Transformation with</span>
              <span className="hero-title-line turquoise-text-gradient">Generative & Agentic AI</span>
            </h1>
            
            <p className="hero-subtitle mb-8">
              Unlock operational excellence today. Our expert consulting services blend Six Sigma methodologies, AI automation, and robust risk management—including full ISO implementation and compliance support. We empower your organization to transform through lean process creation, guaranteed regulatory adherence, and the delivery of measurable, bottom-line results for sustained growth.
            </p>
            
            {/* Animated Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="stat-item">
                <div className="stat-number" data-count="21">21+</div>
                <div className="stat-label">Years Experience</div>
              </div>
              <div className="stat-item">
                <div className="stat-number" data-count="60">60+</div>
                <div className="stat-label">Projects Led</div>
              </div>
              <div className="stat-item">
                <div className="stat-number" data-count="500">500+</div>
                <div className="stat-label">Professionals Trained</div>
              </div>
              <div className="stat-item">
                <div className="stat-number" data-count="100">100%</div>
                <div className="stat-label">Success Rate</div>
              </div>
            </div>
            
            {/* Enhanced CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6 justify-center lg:justify-start">
              <Button 
                className="btn-primary-enhanced"
                onClick={() => window.location.href = '/book-consultation'}
              >
                <Calendar size={20} />
                Book Strategy Call
                <ArrowRight size={20} />
              </Button>
              
              <Button 
                className="btn-primary-enhanced"
                onClick={() => document.querySelector('#case-studies')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View Success Stories
                <ArrowRight size={20} />
              </Button>
            </div>
            
            {/* Enhanced Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center lg:justify-start">
              <Button 
                className="btn-primary-enhanced"
                onClick={openWhatsApp}
              >
                <MessageCircle size={20} />
                WhatsApp Chat
                <ArrowRight size={20} />
              </Button>
              <Button 
                className="btn-primary-enhanced"
                onClick={() => window.open('https://www.linkedin.com/in/shunmugavels', '_blank')}
              >
                <Linkedin size={20} />
                Connect on LinkedIn
                <ArrowRight size={20} />
              </Button>
              <Button 
                className="btn-primary-enhanced"
                onClick={() => window.location.href = '/book-consultation'}
              >
                <Calendar size={20} />
                Free Consultation
                <ArrowRight size={20} />
              </Button>
            </div>
          </div>

          {/* Right side - Enhanced Visual (5 columns) */}
          <div className="lg:col-span-5 relative px-4 lg:px-0">
            {/* Large Logo in Top Right Corner - Responsive sizing */}
            <div className="absolute -top-8 -right-2 md:-top-12 md:-right-4 lg:-top-16 lg:-right-8 z-20">
              <div className="w-20 h-20 md:w-28 md:h-28 lg:w-40 lg:h-40 rounded-lg p-2 md:p-3 lg:p-4">
                <img 
                  src="/shanso-logo-transparent.png"
                  alt="ShansoConsult Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            
            <div className="hero-visual-container">
              {/* Glass Card with Photo */}
              <div className="glass-card">
                <div className="video-container relative">
                  {/* Professional Photo Display */}
                  <img 
                    src="/professional-photo.png"
                    alt="Shunmugavel Subramanian - Process Excellence Expert"
                    className="hero-photo-enhanced max-h-[400px] md:max-h-[500px] lg:max-h-none w-full object-cover"
                    id="hero-photo"
                  />
                </div>
                
                {/* Professional Info Card */}
                <div className="professional-info-card">
                  <h3 className="professional-name">Shunmugavel Subramanian</h3>
                  
                  {/* Professional Credentials & Expertise */}
                  <div className="credentials-showcase">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div className="credential-badge ai-automation">
                        <div className="credential-icon">🤖</div>
                        <div className="credential-content">
                          <div className="credential-title">Gen AI & Agentic AI</div>
                          <div className="credential-subtitle">Strategy Consultant</div>
                        </div>
                      </div>
                      
                      <div className="credential-badge six-sigma">
                        <div className="credential-icon">⚫</div>
                        <div className="credential-content">
                          <div className="credential-title">Six Sigma</div>
                          <div className="credential-subtitle">Black Belt</div>
                        </div>
                      </div>
                      
                      <div className="credential-badge iso-auditor">
                        <div className="credential-icon">🛡️</div>
                        <div className="credential-content">
                          <div className="credential-title">ISO Auditor</div>
                          <div className="credential-subtitle">[9001, 42001, 27001]</div>
                        </div>
                      </div>
                      
                      <div className="credential-badge lean-expert">
                        <div className="credential-icon">🔄</div>
                        <div className="credential-content">
                          <div className="credential-title">Lean</div>
                          <div className="credential-subtitle">Practitioner</div>
                        </div>
                      </div>
                      
                      <div className="credential-badge opex-manager">
                        <div className="credential-icon">📈</div>
                        <div className="credential-content">
                          <div className="credential-title">OpEx & Risk</div>
                          <div className="credential-subtitle">Assessment Manager</div>
                        </div>
                      </div>
                      
                      <div className="credential-badge multi-industry">
                        <div className="credential-icon">🌐</div>
                        <div className="credential-content">
                          <div className="credential-title">Multi-Industry</div>
                          <div className="credential-subtitle">Expert</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <div className="scroll-arrow">
            <ArrowRight size={20} className="rotate-90" />
          </div>
          <span className="scroll-text">Discover Our Services</span>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;